<template>
  <label>
    <input
      type="radio"
      v-model="selectedOption"
      :id="ariaId"
      :data-id="id"
      :value="inputValue"
      :disabled="isDisabled"
    />
    <UiLabel
      class="radio-container"
      :class="['radio-label-' + labelPosition]"
      :text="label"
      :for="ariaId"
    />
  </label>
</template>

<script lang="ts" setup>
import UiLabel from '@sephora-asia/ui-label/UiLabel.vue'
import { LabelPositionOpts } from './types'
import { computed } from 'vue'
import type { WritableComputedRef } from 'vue'

interface Props {
  id: string
  inputValue?: string // When :value is equal to modelValue value, the radio button is checked.
  label?: string
  labelPosition?: LabelPositionOpts
  isDisabled?: boolean
  modelValue?: string | number // binding value from parent component v-model; .e.g <UiRadioButton v-model="selectedOption" />
  isForceChecked?: boolean
}
interface Emits {
  (e: 'update:selectedOption', value: string | number): void
}

const props = withDefaults(defineProps<Props>(), {
  inputValue: '',
  label: '',
  labelPosition: LabelPositionOpts.RIGHT,
  isDisabled: false,
  modelValue: undefined,
  isForceChecked: false,
})
const emit = defineEmits<Emits>()

const selectedOption: WritableComputedRef<string | number> = computed({
  get(): string | number {
    // Prevents modelValue === inputValue = true, causing radio button to be checked
    if (props.modelValue === undefined) return NaN
    if (props.isForceChecked) return props.inputValue
    return props.modelValue
  },
  set(value: string | number): void {
    emit('update:selectedOption', value)
  },
})

const ariaId = computed(() => {
  return `${props.id}-${props.inputValue}`
})
</script>

<style lang="scss" scoped>
@mixin hide-input {
  // Hide while allowing accessibility
  opacity: 0;
  height: 0;
  width: 0;
  position: absolute;
}

@mixin radio-input-base-template {
  content: '';
  border: var(--radio-border-size) solid var(--radio-border-color);
  border-radius: 50%;
  background-color: var(--radio-bg-color);
  aspect-ratio: 1 / 1;
  height: var(--size-typography-size-20);
}
@mixin radio-default {
  --radio-border-size: 1px;
  --radio-border-color: var(--color-base-grey-30);
  --radio-bg-color: var(--color-base-white);
}
@mixin radio-checked {
  --radio-border-size: 6px;
  --radio-border-color: var(--color-base-black);
}
@mixin radio-disabled {
  --radio-bg-color: var(--color-base-grey-05);
  --radio-border-color: var(--color-base-grey-10);
}
@mixin radio-disabled-checked {
  --radio-border-size: 6px;
  --radio-border-color: var(--color-base-grey-10);
  --radio-bg-color: var(--color-base-white);
}

label {
  user-select: none;
  display: flex; // Prevent inline from affecting the vertical spacing around element
  width: inherit;
  height: inherit;
  align-items: center;

  cursor: pointer;
  &:has(input:disabled) {
    cursor: default;
  }

  input {
    @include hide-input;

    // Accessibility
    &:focus-visible ~ .radio-container {
      outline: 5px auto -webkit-focus-ring-color;
      outline-offset: 2px;
    }

    & ~ .radio-container {
      @include radio-default;
    }
    &:checked ~ .radio-container {
      @include radio-checked;
    }
    &:disabled ~ .radio-container {
      @include radio-disabled;
    }
    &:disabled:checked ~ .radio-container {
      @include radio-disabled-checked;
    }
  }

  .radio-container {
    display: flex;
    gap: 8px;
    width: 100%;
  }

  .radio-label-right {
    &::before {
      @include radio-input-base-template;
    }
  }

  .radio-label-left {
    justify-content: space-between;
    &::after {
      @include radio-input-base-template;
    }
  }
}
</style>
